// Base libs
import moment from "moment";
import {
    AssessmentFormQuestionTypeEnum,
    EvaluationQuestionTypeEnum,
    SurveyQuestionTypeEnum,
} from "@genesys/cloud-frontend/dist/mjs/models";

// Base commons
import type { RootState } from "app/store";
import FormType from "enums/FormType";
import { DefaultLimits } from "constants/DefaultLimits";

// Form editor slices
import {
    selectVisibleQuestionGroupIds,
    selectVisibleQuestionIds,
} from "../qualityForm/qualityFormSlice";
import { selectFormType } from "features/qualityForm/qualityFormSlices/qualityFormCommons";

export const COMMENTS_CHARACTER_LIMIT_EFFECTIVE_DATE =
    "2024-02-19T00:00:00.000Z";

// checks if all questions within a question group are answered
export const selectIsScorableQuestionGroupValid = (
    state: RootState,
    questionGroupId: string
) => {
    const questionGroup =
        state.scorable.data.questionGroupScores[questionGroupId];
    if (questionGroup?.markedNA) {
        return true;
    }
    const visibibleQuestionIds = selectVisibleQuestionIds(
        state,
        questionGroupId
    );
    return visibibleQuestionIds.every((questionId) => {
        return selectIsScorableQuestionValid(state, questionId);
    });
};

export const selectIsScorableQuestionValid = (
    state: RootState,
    questionId: string
) => {
    const questionDetails = state.qualityForm.data.questions?.[questionId];
    const isFreeText =
        questionDetails?.type ===
            AssessmentFormQuestionTypeEnum.FreeTextQuestion ||
        questionDetails?.type === SurveyQuestionTypeEnum.FreeTextQuestion;
    const isReadOnly =
        questionDetails?.type ===
        SurveyQuestionTypeEnum.ReadOnlyTextBlockQuestion;

    if (isReadOnly) {
        return true;
    }

    return (
        selectIsScorableQuestionAnswered(state, questionId) &&
        selectIsAdditionalCommentValid(state, questionId) &&
        (isFreeText ? selectIsFreeTextAnswerValid(state, questionId) : true) &&
        (questionDetails?.type === SurveyQuestionTypeEnum.NpsQuestion
            ? selectIsNPSAnswerValid(state, questionId)
            : true)
    );
};
export const selectIsFreeTextAnswerUnderLimit = (
    state: RootState,
    questionId: string
) => {
    if (selectFormType(state) === FormType.ASSESSMENT) {
        return true;
    }
    const questionScore = state.scorable.data.questionScores?.[questionId];
    const maxAllowedChars =
        state.qualityForm.data.questions?.[questionId]?.maxResponseCharacters ??
        DefaultLimits.initMaxResponseCharacters;

    return (questionScore?.freeTextAnswer ?? "").length <= maxAllowedChars;
};
export const selectIsFreeTextAnswerValid = (
    state: RootState,
    questionId: string
) => {
    const questionScore = state.scorable.data.questionScores?.[questionId];
    const question = state.qualityForm.data.questions?.[questionId];
    if (
        questionScore &&
        !(questionScore.markedNA ?? false) &&
        !(question?.naEnabled ?? false)
    ) {
        return selectIsFreeTextAnswerUnderLimit(state, questionId);
    }
    return true;
};

export const selectIsNPSAnswerValid = (
    state: RootState,
    questionId: string
) => {
    return (
        selectIsNPSAnswerExplanationUnderLimit(state, questionId) &&
        selectIsNPSExplanationPresentIfRequired(state, questionId)
    );
};

export const selectIsNPSAnswerExplanationUnderLimit = (
    state: RootState,
    questionId: string
) => {
    const questionScore = state.scorable.data.questionScores?.[questionId];
    const maxAllowedChars =
        state.qualityForm.data.questions?.[questionId]?.maxResponseCharacters ??
        DefaultLimits.initMaxResponseCharacters;
    if (
        questionScore &&
        !(questionScore.markedNA ?? false) &&
        (questionScore.npsTextAnswer ?? "").length > maxAllowedChars
    ) {
        return false;
    }
    return true;
};

export const selectIsNPSExplanationPresentIfRequired = (
    state: RootState,
    questionId: string
) => {
    const questionScore = state.scorable.data.questionScores?.[questionId];
    const question = state.qualityForm.data.questions?.[questionId];
    const isExplanationRequired =
        (question?.explanationPrompt ?? "").length > 0;
    const isQuestionNaEnabled = question?.naEnabled ?? false;
    if (questionScore && isExplanationRequired && !isQuestionNaEnabled) {
        return (
            questionScore?.npsTextAnswer !== undefined &&
            questionScore?.npsTextAnswer.trim().length > 0
        );
    }
    return true;
};
export const selectIsScorableQuestionAnswered = (
    state: RootState,
    questionId: string
) => {
    const questionScore = state.scorable.data.questionScores[questionId];
    const questionData = state.qualityForm.data.questions?.[questionId];
    const questionType = questionData?.type;
    const isReadOnly =
        questionType === SurveyQuestionTypeEnum.ReadOnlyTextBlockQuestion;
    const isMultipleSelect =
        questionType === EvaluationQuestionTypeEnum.MultipleSelectQuestion;

    if ((questionScore?.markedNA && questionData?.naEnabled) || isReadOnly) {
        return true;
    }
    if (
        (questionType === SurveyQuestionTypeEnum.FreeTextQuestion ||
            questionType === AssessmentFormQuestionTypeEnum.FreeTextQuestion) &&
        questionData?.naEnabled
    ) {
        return true;
    }
    if (isMultipleSelect) {
        // even if there's no answer selected for this question, we will allow the submission + save
        return true;
    }
    return (
        (questionScore?.answerId && questionScore?.answerId !== "") ||
        (questionScore?.freeTextAnswer?.trim()?.length ?? 0) > 0 ||
        questionScore?.npsScore !== undefined
    );
};
export const selectIsAdditionalCommentValid = (
    state: RootState,
    questionId: string
) => {
    if (state.scorable.data.questionScores[questionId]?.markedNA) {
        return true;
    }
    const requiresComment =
        state.qualityForm.data.questions?.[questionId].commentsRequired ??
        false;
    if (requiresComment) {
        const questionScore = state.scorable.data.questionScores[questionId];
        return (questionScore?.comments ?? "").length > 0;
    }
    return true;
};

export const selectIsScorableFormValid = (state: RootState) => {
    return selectVisibleQuestionGroupIds(state).every((questionGroupId) =>
        selectIsScorableQuestionGroupValid(state, questionGroupId)
    );
};

export const selectCalibrationEvaluators = (state: RootState) =>
    state.scorable.data.scorable?.calibration?.evaluators;

export const selectAreSurveyAnswersUnderLimit = (state: RootState) => {
    if (moment().isBefore(moment(COMMENTS_CHARACTER_LIMIT_EFFECTIVE_DATE))) {
        // Skip the check if current date is before effective date
        return true;
    }
    return selectVisibleQuestionGroupIds(state).every((questionGroupId) =>
        selectVisibleQuestionIds(state, questionGroupId).every((questionId) => {
            const questionDetails =
                state.qualityForm.data.questions?.[questionId];
            if (questionDetails?.type === SurveyQuestionTypeEnum.NpsQuestion) {
                // NPS prompt
                return selectIsNPSAnswerExplanationUnderLimit(
                    state,
                    questionId
                );
            } else if (
                questionDetails?.type ===
                SurveyQuestionTypeEnum.FreeTextQuestion
            ) {
                // Free Text
                return selectIsFreeTextAnswerUnderLimit(state, questionId);
            } else {
                return true;
            }
        })
    );
};

// Would be added with more validations.
export const selectIsScorableValid = (state: RootState) => {
    return selectIsScorableFormValid(state);
};
